import { CustomInput } from "global/components";
import { AppModal } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
  sanitizeSignerEmail,
  toPhoneParts,
} from "helpers/methods";
import {
  RequestFile,
  RequestParticipant,
  SignRequest,
} from "models/interfaces";
import {
  removeFileFromSignRequest,
  setChipLoader,
  setErrors,
  setLoading,
  setReduxSignRequest,
  setUploadedFileNames,
  setUploadedFileNamesDelete,
  updateFileInSignRequest,
  updateFileWithFields,
} from "redux/appSlice";
import { validateField } from "screens/SignRequest/ParticipantScreen/ParticipantScreenService";
import {
  copyFiles,
  getEnvelopeId,
  saveTemplate,
} from "screens/SignRequest/SignRequestService";
import { validateAndNormalizeSignRequestFieldTypes } from "screens/SignRequest/SignRequestHelper";
import FolderTree from "screens/Templates/FolderTree";
import { hasAccessTo } from "layouts/Config";
import {
  embedDeliveryModeOf,
  isEmbedEditorMode,
} from "embed/EmbedEditorMode";

/**
 * Participant field limits, in ONE place.
 *
 * These used to be duplicated: the styling check allowed 200 per name part
 * while the message check allowed 100, so a name of 101-200 characters got red
 * styling with no explanation, or an explanation with no styling, depending on
 * which prop the caller read. Both now derive from the same numbers.
 */
export const PARTICIPANT_NAME_PART_MAX = 100;
export const PARTICIPANT_NAME_MAX = 200;
/** RFC 5321 caps an address at 254 characters; the DB columns are varchar(255). */
export const PARTICIPANT_EMAIL_MAX = 254;

export const getParticipantNameError = (name: string): string | null => {
  if (!name) return null;
  const parts = name.split(" ");
  const firstName = parts[0] || "";
  const lastName = name.substring(firstName.length).trim();
  if (firstName.length > PARTICIPANT_NAME_PART_MAX)
    return `First name must not exceed ${PARTICIPANT_NAME_PART_MAX} characters.`;
  if (lastName.length > PARTICIPANT_NAME_PART_MAX)
    return `Last name must not exceed ${PARTICIPANT_NAME_PART_MAX} characters.`;
  if (name.length > PARTICIPANT_NAME_MAX)
    return `Name must not exceed ${PARTICIPANT_NAME_MAX} characters.`;
  return null;
};

/** Styling and message now answer to the same rule -- see the note above. */
export const isValidParticipantNameLength = (name: string): boolean =>
  getParticipantNameError(name) === null;

/**
 * Length rule for a participant email. Kept separate from the format check so
 * the length can be reported while the user is still typing, the way the name
 * limit is, without nagging about format on every keystroke.
 */
export const getParticipantEmailError = (email: string): string | null => {
  if (!email) return null;
  if (email.length > PARTICIPANT_EMAIL_MAX)
    return `Email must not exceed ${PARTICIPANT_EMAIL_MAX} characters.`;
  return null;
};

interface SaveTemplatePopupsProps {
  signRequest: SignRequest;
  folderTree: any[];
  newTemplate?: boolean;

  errors: Record<string, string>;
  dispatch: any;

  saveTemplatePopup: boolean;
  setSaveTemplatePopup: (v: boolean) => void;

  showFolderTreePopup: boolean;
  setShowFolderTreePopup: (v: boolean) => void;

  destFolder: any;
  setDestFolder: (v: any) => void;

  selectedFolderData: any;
  setSelectedFolderData: (v: any) => void;

  setSignRequest?: (signRequest: SignRequest) => void;
  isRequest: boolean;
}

export const initialState: SignRequest = {
  id: "",
  signers: [],
  files: [],
  emailSubject: "",
  emailMessage: "",
  expirationDays: 180,
  requestedBy: { name: "", email: "" },
  signedBy: "",
  status: "",
  approval: {
    approvalNeeded: false,
    orderNeeded: false,
    reviewers: [],
  },
  reminders: {
    daysBeforeFirstReminder: 5,
    daysBetweenReminders: 3,
  },
  needReminders: false,
  signRequestConfig: {
    attachDocumentToMailOnCompletion: true,
    allowDocumentDownload: true,
    sendNotificationOnCompletion: true,
    attachSingleCertificate: true,
    allowSignatureDelegation: true,
  },
  currentSigners: [],
  requestToken: "",
  autoGeneratedId: 0,
  templateId: null,
};

export const setCoordinates = (request: SignRequest) => {
  const payload = structuredClone(request.files).map(
    (file: any, ind: number) => {
      const dimension = file.dimensions;
      let pageX = 0;
      let pageY = 0;
      let pageWidth = 0;
      let pageHeight = 0;
      for (let field of file.fields) {
        const isText = [
          strings.text,
          strings.email,
          strings.name,
          strings.title,
          strings.companyName.replace(" ", "_"),
          strings.dateSigned.replace(" ", "_"),
        ].includes(field.type);
        const pageDim = dimension[field.page];
        const nudge = isText ? 4 : field.type === "checkbox" ? 2 : 0;

        if (
          isTruthy(field.height) &&
          isTruthy(field.width) &&
          isTruthy(field.top) &&
          isTruthy(field.left)
        ) {
          const rx = pageDim.x / field.width;
          const ry = pageDim.y / field.height;

          if (field.existingField) {
            pageX = (field.x / pageDim.x) * field.width + field.left;
            pageY = (field.y / pageDim.y) * field.height + field.top;
            pageWidth = (field.w / pageDim.x) * field.width;
            pageHeight = (field.h / pageDim.y) * field.height;
          } else {
            pageX = (field.fieldPageX / 100) * field.width + field.left;
            pageY = (field.fieldPageY / 100) * field.height + field.top;
            pageWidth = (field.fieldPageWidth / 100) * field.width;
            pageHeight = (field.fieldPageHeight / 100) * field.height;
          }
          field.x = pageX * rx - field.left * rx + nudge;
          field.y = pageY * ry - field.top * ry + nudge;
          field.h = pageHeight * ry;
          field.w = field.type === "checkbox" ? field.h : pageWidth * rx;
        } else if (
          isTruthy(field.fieldPageX) &&
          isTruthy(field.fieldPageY) &&
          isTruthy(field.fieldPageWidth) &&
          isTruthy(field.fieldPageHeight)
        ) {
          field.x = (field.fieldPageX / 100) * pageDim.x + nudge;
          field.y = (field.fieldPageY / 100) * pageDim.y + nudge;
          field.h = (field.fieldPageHeight / 100) * pageDim.y;
          field.w =
            field.type === "checkbox"
              ? field.h
              : (field.fieldPageWidth / 100) * pageDim.x;
        }

        if (field.type) {
          field.type = field.type.split(" ").join("_").toLowerCase();
        }
      }
      // Preserve existing file identifiers/urls when present, and only fallback
      // to doc variants if pdf variants are missing.
      file.unsignedPdfUrl = file.unsignedPdfUrl || file.unsignedDocUrl;
      file.unsignedPdf = file.unsignedPdf || file.unsignedDoc;
      return file;
    },
  );
  const result = { ...request, files: payload };
  if (result.signers?.length) {
    result.signers = result.signers.map((s: any) => {
      const next: any = {
        ...s,
        email: sanitizeSignerEmail(s.email) || s.email,
      };

      // SMS Delivery — channels only apply to remote (non in-person) signers,
      // and never to CC recipients. Drop stray channels/phone otherwise.
      const isInPerson = s.deliveryMode === strings.deliveryModes.inPerson;

      // Delivery mode is stated outright for every recipient, never inferred.
      // Only a row the requester actually marked "Sign in person" goes out as
      // `IN_PERSON`; everyone else — email-link signers and CC recipients
      // alike — goes out as an explicit `EMAIL`. Leaving it undefined leaned
      // on the backend's "missing means EMAIL" default, and any stray value
      // that survived on the row (a copied envelope, an older payload) then
      // read as in-person: that signer is never mailed a link and is offered
      // to the host for hand-off instead. Writing `EMAIL` also *repairs* such
      // a row on the next save rather than leaving the bad value in place.
      //
      // This sits in `setCoordinates` deliberately — every send, update,
      // template save and self-sign funnels through here, so the edit path
      // (`updateSignRequest`) cannot reintroduce it on an already-correct
      // envelope.
      //
      // The embedded wizard is the one exception: a signer there may sign in
      // the partner's app (`EMBEDDED`, as the partner set it; the server
      // applies the same rule), and nothing Inkfree sends applies to them.
      // Outside it nothing is ever sent as `EMBEDDED`, so a copied embedded
      // envelope is still repaired to `EMAIL`.
      const isEmbedded =
        isEmbedEditorMode() &&
        !s.cc &&
        !isInPerson &&
        embedDeliveryModeOf(s.deliveryMode) === strings.deliveryModes.embedded;
      next.deliveryMode = isInPerson
        ? strings.deliveryModes.inPerson
        : isEmbedded
          ? strings.deliveryModes.embedded
          : strings.deliveryModes.email;

      if (s.cc || isInPerson || isEmbedded) {
        delete next.deliveryChannels;
        delete next.phone;
      }

      // Phone is sent as { countryCode, number }; omit when blank / dial-code
      // only. Only meaningful when a non-email channel (SMS) is selected.
      const phoneParts =
        !s.cc && !isInPerson && !isEmbedded ? toPhoneParts(s.phone) : undefined;
      if (phoneParts) next.phone = phoneParts;
      else delete next.phone;

      // Auth phone (Phone authentication / SMS OTP) — same split object, null
      // when not phone auth or blank.
      if (s.requestSecurity) {
        next.requestSecurity = {
          ...s.requestSecurity,
          authPhone: toPhoneParts(s.requestSecurity.authPhone) ?? null,
        };
      }

      return next;
    });
  }
  if (result.approval?.reviewers?.length) {
    result.approval = {
      ...result.approval,
      reviewers: result.approval.reviewers.map((r: any) => ({
        ...r,
        email: sanitizeSignerEmail(r.email) || r.email,
      })),
    };
  }
  return result;
};

export const updateEnvelopId = async (
  dispatch: Function,
  signRequestId: string,
  fromRequest: boolean,
  isRequest: boolean,
  setIsFileUploaded?: Function,
) => {
  try {
    const isSignAccess = hasAccessTo(
      strings.resources.sign,
      strings.sendPermission,
    );
    if (isRequest && !isSignAccess) {
      return;
    }
    dispatch(setChipLoader(true));
    const res = await getEnvelopeId();
    await copyFiles(signRequestId, res.message, fromRequest, isRequest);
    dispatch(setReduxSignRequest({ id: res.message }));
    dispatch(setChipLoader(false));
    setIsFileUploaded && setIsFileUploaded(true);
    dispatch(setChipLoader(false));
    return res.message;
  } catch (error: any) {
    dispatch(setChipLoader(false));
    setIsFileUploaded && setIsFileUploaded(true);
    handleErrorMessage(error);
  }
};

export const uploadCopiedFiles = async (
  signRequest: SignRequest,
  setIsFileUploaded: Function,
  fromRequest: boolean,
  needCopy: boolean = false,
  dispatch: Function,
  isRequest: boolean,
) => {
  try {
    let envelopeId;
    if (needCopy) {
      envelopeId = await updateEnvelopId(
        dispatch,
        signRequest.id,
        fromRequest,
        isRequest,
        setIsFileUploaded,
      );
    }
    // dispatch(setIsRequest(fromRequest));
    if (isRequest) {
      for (let file of signRequest.files) {
        dispatch(setUploadedFileNames([file.unsignedDoc]));
        addFile(file, isRequest, dispatch, envelopeId, false);
      }
    }
    setIsFileUploaded && setIsFileUploaded(true);
  } catch (error: any) {
    for (let file of signRequest.files) {
      dispatch(removeFileFromSignRequest(file));
      dispatch(setUploadedFileNamesDelete(file.unsignedDoc));
    }
    setIsFileUploaded && setIsFileUploaded(true);
    handleErrorMessage(error);
  }
};

export const addFile = async (
  file: RequestFile,
  isRequest: boolean,
  dispatch: Function,
  envelopeId: string,
  isPublic: boolean,
  abortSignal?: AbortSignal,
) => {
  try {
    let requestFile: RequestFile = {
      unsignedPdf: file.unsignedPdf,
      unsignedPdfUrl: file.unsignedPdfUrl,
      signedDoc: file.signedDoc,
      fields: file.fields,
      pageCount: file.pageCount,
      pageUrls: {},
      dimensions: file.dimensions,
      unsignedDocUrl: file.unsignedPdfUrl || file.unsignedDocUrl,
      unsignedDoc: file.unsignedPdf || file.unsignedDoc,
      isUploading: false,
    };
    dispatch(setLoading(true));
    dispatch(updateFileInSignRequest(requestFile));
    dispatch(updateFileWithFields(requestFile));
  } catch (error: any) {
    handleErrorMessage(error);
    dispatch(removeFileFromSignRequest(file));
    dispatch(setUploadedFileNamesDelete(file.unsignedPdf));
  } finally {
    dispatch(setLoading(false));
  }
};

export const convertFileForBackend = (file: RequestFile) => {
  return {
    ...file,
    unsignedPdf: file.unsignedPdf || file.unsignedDoc,
    unsignedPdfUrl: file.unsignedPdfUrl || file.unsignedDocUrl,
  };
};

export const areAllFieldsFilled = (
  signRequest: SignRequest,
  setAllFieldsFilled: Function,
  setAllRequiredFieldsFilled: Function,
): number[] => {
  let fieldIds = [];
  let allRequiredFieldsFilled = true;
  let allFieldsFilled = true;

  for (const file of signRequest.files) {
    for (const field of file.fields) {
      if (field.required && (!field.value || field.value === "")) {
        fieldIds.push(field.id);
        allRequiredFieldsFilled = false;
      }
      if (
        (!field.value || field.value === "") &&
        (field.type === "signature" || field.type === "initials")
      ) {
        allFieldsFilled = false;
        fieldIds.push(field.id);
      }
      if (!validateField(field)) {
        allFieldsFilled = false;
      }
    }
  }

  setAllRequiredFieldsFilled(allRequiredFieldsFilled);
  setAllFieldsFilled(allFieldsFilled);

  return fieldIds;
};

export const signerFieldValidationPatterns = {
  Numbers: /^[\d\s\r\n]+$/,
  Letters: /^[a-zA-Z\s\r\n]+$/,
  SSN: /^\d{3}-\d{2}-\d{4}$/,
  Zip: /^\d{5}$/,
  "Zip+4": /^\d{5}-\d{4}$/,
  none: /.*/, // Match anything for 'none'
};

export const uploadFiles = async (
  signRequest: SignRequest,
  setIsFileUploaded: Function,
  fromRequest: boolean,
  needCopy: boolean = false,
  dispatch: Function,
  isRequest: boolean,
) => {
  try {
    let envelopeId;
    if (needCopy) {
      envelopeId = await updateEnvelopId(
        dispatch,
        signRequest.id,
        fromRequest,
        isRequest,
        setIsFileUploaded,
      );
    }
    // dispatch(setIsRequest(fromRequest));
    if (isRequest) {
      for (let file of signRequest.files) {
        dispatch(setUploadedFileNames([file.unsignedPdf]));
        addFile(file, true, dispatch, envelopeId, false);
      }
    }
    setIsFileUploaded && setIsFileUploaded(true);
  } catch (error: any) {
    for (let file of signRequest.files) {
      dispatch(removeFileFromSignRequest(file));
      dispatch(setUploadedFileNamesDelete(file.unsignedPdf));
    }
    setIsFileUploaded && setIsFileUploaded(true);
    handleErrorMessage(error);
  }
};

export const GetSaveTemplatePopups: React.FC<SaveTemplatePopupsProps> = ({
  signRequest,
  folderTree,
  newTemplate,

  errors,
  dispatch,

  saveTemplatePopup,
  setSaveTemplatePopup,

  showFolderTreePopup,
  setShowFolderTreePopup,

  destFolder,
  setDestFolder,

  selectedFolderData,
  setSelectedFolderData,

  setSignRequest,
  isRequest,
}) => {
  const handleTemplatePopup = async (isRequest: boolean) => {
    try {
      // Validate and normalize field types before saving template
      // This will automatically fix types like "Yvette-date_signed-1" to "date_signed"
      const validationResult =
        validateAndNormalizeSignRequestFieldTypes(signRequest);

      const requestObject = setCoordinates(validationResult.normalizedRequest);
      requestObject.type = strings.template;
      // Templates should be created without prefilled field values.
      requestObject.files = (requestObject.files || []).map(
        (file: RequestFile) => ({
          ...file,
          fields: (file.fields || []).map((field: any) => ({
            ...field,
            value: "",
          })),
        }),
      );

      // Clear security configurations for save as template options
      requestObject.signers = (requestObject.signers || []).map(
        (signer: RequestParticipant) => ({
          ...signer,
          requestSecurity: signer.requestSecurity
            ? {
                ...signer.requestSecurity,
                securityCode: null,
                otp: null,
                otpValidUntil: null,
                isVerified: false,
                verified: false,
                verifiedAt: null,
                failedAttempts: 0,
              }
            : {
                needSecuredAccess: false,
                securityCode: null,
                needOTPValidation: false,
                otp: null,
                otpValidUntil: null,
                isVerified: false,
                verified: false,
                verifiedAt: null,
              },
        }),
      );

      if (requestObject.approval?.reviewers) {
        const clearedReviewers = requestObject.approval.reviewers.map(
          (reviewer: RequestParticipant) => ({
            ...reviewer,
            requestSecurity: reviewer.requestSecurity
              ? {
                  ...reviewer.requestSecurity,
                  securityCode: null,
                  otp: null,
                  otpValidUntil: null,
                  isVerified: false,
                  verified: false,
                  verifiedAt: null,
                  failedAttempts: 0,
                }
              : {
                  needSecuredAccess: false,
                  securityCode: null,
                  needOTPValidation: false,
                  otp: null,
                  otpValidUntil: null,
                  isVerified: false,
                  verified: false,
                  verifiedAt: null,
                },
          }),
        );
        requestObject.approval = {
          ...requestObject.approval,
          reviewers: clearedReviewers,
        };
      }

      const folderRequest: SignRequest = {
        ...requestObject,
        templateName: signRequest.templateName,
        parentId: destFolder ? destFolder.folderId : 0,
        files: requestObject.files.map((file: RequestFile) =>
          convertFileForBackend(file),
        ),
        type: "template",
      };

      if (!signRequest.templateName) {
        dispatch(setErrors({ templateName: "Please Enter Template Name." }));
        return;
      }

      dispatch(setLoading(true));
      await saveTemplate(folderRequest, isRequest);
      dispatch(setLoading(false));
      openSuccessNotification("Template created successfully!");
    } catch (error: any) {
      dispatch(setLoading(false));
      handleErrorMessage(error);
      setSaveTemplatePopup(false);
      setShowFolderTreePopup(false);
    } finally {
      dispatch(setLoading(false));
      setSaveTemplatePopup(false);
      setShowFolderTreePopup(false);
      setDestFolder(null);
      setSignRequest &&
        setSignRequest({
          ...signRequest,
          ...{ templateName: "" },
        });
      dispatch(setReduxSignRequest({ templateName: "" }));
      dispatch(setErrors({ templateName: "" }));
    }
  };

  return (
    <>
      <AppModal
        open={saveTemplatePopup}
        onClose={() => setSaveTemplatePopup(false)}
        title="Do you want to save template?"
        size="sm"
        primaryLabel={!newTemplate ? strings.next : strings.save}
        onPrimary={async () => {
          setSelectedFolderData(null);
          if (!isTruthy(signRequest.templateName)) {
            dispatch(setErrors({ templateName: "Please Enter Template Name." }));
            return;
          }
          dispatch(setErrors({ templateName: "" }));
          if (!newTemplate) {
            setShowFolderTreePopup(true);
          } else {
            await handleTemplatePopup(false);
          }
        }}
        cancelLabel="Cancel"
        onCancel={() => {
          setSaveTemplatePopup(false);
          setSignRequest &&
            setSignRequest({
              ...signRequest,
              ...{ templateName: "" },
            });
          dispatch(setReduxSignRequest({ templateName: "" }));
        }}
      >
        <CustomInput
            required
            id="template-name"
            placeHolder="Enter Template Name"
            type="text"
            name="templateName"
            label="Template Name"
            value={signRequest?.templateName! || ""}
            onChange={(e: React.ChangeEvent<any>) => {
              setSignRequest &&
                setSignRequest({
                  ...signRequest,
                  ...{ [e.target.name]: e.target.value },
                });
              dispatch(
                setReduxSignRequest({ [e.target.name]: e.target.value }),
              );
            }}
          error={
            !isTruthy(signRequest?.templateName!) && errors?.templateName!
          }
        />
      </AppModal>

      <AppModal
        open={showFolderTreePopup}
        onClose={() => {
          dispatch(setErrors({ templateName: "" }));
          dispatch(setReduxSignRequest({ templateName: "" }));
          setShowFolderTreePopup(false);
          setDestFolder(null);
        }}
        title="Select Folder"
        size="md"
        primaryLabel={strings.save}
        onPrimary={async () => {
          if (destFolder) {
            dispatch(setReduxSignRequest({ parentId: destFolder.folderId }));
          }
          setShowFolderTreePopup(false);
          await handleTemplatePopup(isRequest);
        }}
        cancelLabel="Cancel"
        onCancel={() => {
          dispatch(setErrors({ templateName: "" }));
          dispatch(setReduxSignRequest({ templateName: "" }));
          setShowFolderTreePopup(false);
          setDestFolder(null);
        }}
      >
        <FolderTree
          folderData={folderTree}
          destFolder={destFolder}
          setDestFolder={setDestFolder}
          selectedFolderData={selectedFolderData}
          setSelectedFolderData={setSelectedFolderData}
          isShowMoveFolderName={false}
        />
      </AppModal>
    </>
  );
};
